import React, { useState, Fragment } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import paths from 'app/paths'
import Page from 'contexts/page'
import { useAccess } from 'contexts/access'
import TasksProvider, { useTasks } from 'contexts/tasks'
import { useLocationStateIntent } from 'hooks/location-intent'
import { useQueryResolveItem } from 'hooks/resolve-item'
import { Tasks } from './s'
import HeroActions from 'components/hero-actions'
import Filter from 'pages/tasks/filter'
import { Ghost, ButtonArrow } from 'components/button'
import Modal from 'modals/dialog'
import ActingTask from 'pages/tasks/modals/task'
import AddToCalendar from 'components/add-to-calendar'

const YourTasksPane = () => {
    const { formatMessage } = useIntl()

    const navigate = useNavigate()

    const { checkModule } = useAccess()
    const meetingsAccess = checkModule('performance')

    const {
        tasks,
        hasFetched,

        fetchTask,
        toggleTaskCompleted,
        removeTask
    } = useTasks()

    const [acting, setActing] = useState(null)
    const [addingToCalendar, setAddingToCalendar] = useState(null)

    useQueryResolveItem({
        items: tasks,
        fetch: fetchTask,
        onResolved: (entry, clearQuery) => {
            entry && viewAction(entry)?.onClick()
            clearQuery()
        }
    })

    const reset = () => {
        setActing(null)
    }

    useLocationStateIntent({
        intent: 'add-task',
        action: () => setActing({ mode: 'edit' })
    })

    const salt = 'profile:tasks'

    const viewAction = task => ({
        salt: `${salt}:view`,
        label: formatMessage({
            id: 'action_view_details',
            defaultMessage: 'View details'
        }),
        effect: 'neutral',
        onClick: () => setActing({
            mode: 'view',
            task
        })
    })

    const editAction = task => ({
        salt: `${salt}:edit`,
        label: formatMessage({
            id: 'task_action_edit',
            defaultMessage: 'Edit task'
        }),
        effect: 'neutral',
        onClick: () => setActing({
            mode: 'edit',
            task
        })
    })

    const toggleCompletedAction = ({ id, completedAt }) => ({
        salt: `${salt}:toggle-completed`,
        label: formatMessage({
            id: completedAt ?
                'task_action_mark_uncompleted' :
                'task_action_mark_completed',
            defaultMessage: completedAt ?
                'Mark as uncompleted' :
                'Mark as completed'
        }),
        onClick: () => toggleTaskCompleted(id),
        effect: 'neutral'
    })

    const addToCalendarAction = ({ dueAt, title, description }) => ({
        salt: `${salt}:add-to-calendar`,
        label: formatMessage({
            id: 'action_add_to_calendar',
            defaultMessage: 'Add to calendar'
        }),
        effect: 'neutral',
        onClick: () => setAddingToCalendar({
            start: dueAt,
            end: dueAt,
            title,
            description
        })
    })

    const createMeetingAction = ({ parent }) => {
        if(!meetingsAccess) {
            return null
        }

        return ({
            salt: `${salt}:create-meeting`,
            label: formatMessage({
                id: 'meeting_action_add',
                defaultMessage: 'New meeting'
            }),
            effect: 'neutral',
            onClick: () => navigate(paths.meetings.base, {
                state: {
                    intent: 'create-event',
                    concerns: (parent?.concerns?.type === 'user') ?
                        parent?.concerns :
                        null
                }
            })
        })
    }

    const removeAction = ({ id }) => ({
        salt: `${salt}:delete`,
        label: formatMessage({
            id: 'action_delete',
            defaultMessage: 'Delete'
        }),
        effect: 'destructive',
        prompt: {
            question: formatMessage({
                id: 'task_action_delete_prompt_question',
                defaultMessage: 'Confirm removing this task'
            }),
            confirmText: formatMessage({
                id: 'action_delete',
                defaultMessage: 'Delete'
            })
        },
        onClick: () => removeTask(id)
    })

    const actions = {
        view: viewAction,
        edit: editAction,
        toggleCompleted: toggleCompletedAction,
        addToCalendar: addToCalendarAction,
        createMeeting: createMeetingAction,
        remove: removeAction
    }

    return (
        <>
            <HeroActions>
                <Filter salt={salt} />
                <Fragment>
                    <Ghost
                        onClick={() => setActing({ mode: 'edit' })}
                        className="constructive">
                        <FormattedMessage
                            id="task_action_add"
                            defaultMessage="Add task" />
                    </Ghost>
                    {(!tasks?.length && !!hasFetched) && (
                        <ButtonArrow />
                    )}
                </Fragment>
            </HeroActions>
            <Tasks
                header={(!!tasks?.length && hasFetched)}
                actions={actions} />
            <Modal
                show={!!acting}
                dismiss={reset}
                className="scrollable"
                salt={salt}>
                <ActingTask
                    {...acting}
                    actions={actions}
                    onDone={reassigned => {
                        reset()

                        if(reassigned?.assignedGroup || reassigned?.assignedTo) {
                            setActing({
                                mode: 'reassigned',
                                reassigned
                            })
                        }
                    }}
                    salt={`${salt}:act`} />
            </Modal>
            <Modal
                show={!!addingToCalendar}
                dismiss={() => setAddingToCalendar(null)}
                className="scrollable"
                salt={`${salt}:add-to-calendar`}>
                <AddToCalendar {...addingToCalendar} />
            </Modal>
        </>
    )
}

export default ({ title, upgradable, ...props }) => (
    <Page
        view="tasks_me"
        title={title}
        upgrade={{
            enabled: upgradable,
            tutorial: {
                which: 'tasks',
                upgrade: { module: 'tasks' }
            }
        }}>
        <TasksProvider
            fetchAccess={!upgradable}
            filter={{ assignedTo: 'me' }}>
            <YourTasksPane {...props} />
        </TasksProvider>
    </Page>
)